import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { fmtErr } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, X } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function Vault() {
  const nav = useNavigate();
  const { t } = useTranslation();
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);
  const fileRef = useRef(null);

  // Vault authentication is entirely server-side now:
  //   * `access_token` HttpOnly cookie authenticates the JWT user
  //   * `vault_session` HttpOnly cookie authorizes vault media access (15min)
  // No client-side "vaultUnlocked" flag is trusted — we simply issue the call
  // and let the backend 401. The 401 handler redirects to /vault/pin.
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/vault/media");
      setMedia(data.media);
    } catch (e) {
      if (e?.response?.status === 401) { nav("/vault/pin"); return; }
      toast.error(fmtErr(e));
    } finally { setLoading(false); }
  }, [nav]);

  useEffect(() => { load(); }, [load]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting same file
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error(t("vault.max2mb"));
      return;
    }
    const kind = file.type.startsWith("video") ? "video" : "image";
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post("/vault/media", { data_url: reader.result, kind });
        toast.success(t("vault.addedToVault"));
        load();
      } catch (err) {
        if (err?.response?.status === 401) { nav("/vault/pin"); return; }
        toast.error(fmtErr(err));
      }
    };
    reader.readAsDataURL(file);
  };

  const remove = async (id) => {
    if (!window.confirm(t("vault.removeConfirm"))) return;
    try {
      await api.delete(`/vault/media/${id}`);
      load();
    } catch (e) {
      if (e?.response?.status === 401) { nav("/vault/pin"); return; }
      toast.error(fmtErr(e));
    }
  };

  // Server clears the vault_session cookie; we then leave the page.
  const lock = async () => {
    try { await api.post("/vault/lock"); } catch { /* server-side clear is best-effort — cookie will expire in ≤15min regardless */ }
    nav("/home");
  };

  return (
    <div className="min-h-screen bg-black text-white relative">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(230,0,0,0.15)_0%,transparent_70%)] pointer-events-none" />
      <div className="relative max-w-md mx-auto px-5 pt-6 pb-24">
        <div className="flex items-center justify-between" data-testid="vault-header">
          <Link to="/home" onClick={lock} className="flex items-center gap-2 text-zinc-300 hover:text-white" data-testid="vault-back">
            <ArrowLeft size={18} className="rtl:rotate-180"/> {t("vault.back")}
          </Link>
          <span className="font-mono-accent text-zinc-400">{t("vault.vaultLabel")}</span>
          <span className="w-12" />
        </div>

        <h1 className="mt-5 text-5xl font-bold tracking-tight" style={{ fontFamily: "'Manrope',sans-serif", fontWeight: 800 }}>{t("vault.title")}</h1>
        <p className="mt-2 text-zinc-400">{t("vault.subtitle")}</p>

        <div className="mt-8 grid grid-cols-3 gap-3" data-testid="vault-grid">
          {loading && Array.from({length: 9}).map((_,i)=>(
            <div key={`skeleton-${i}`} className="aspect-square rounded-2xl bg-black/40 border border-white/5" />
          ))}
          {!loading && media.length === 0 && Array.from({length: 9}).map((_,i)=>(
            <div key={`placeholder-${i}`} className="aspect-square rounded-2xl bg-black/40 border border-white/5" />
          ))}
          {!loading && media.map(m => (
            <button
              key={m.id}
              onClick={()=>setViewing(m)}
              data-testid={`vault-media-${m.id}`}
              className="aspect-square rounded-2xl overflow-hidden border border-white/10 hover:border-[#E60000]/60 transition relative group"
            >
              {m.kind === "video"
                ? <video src={m.data_url} className="w-full h-full object-cover" muted />
                : <img src={m.data_url} alt="" className="w-full h-full object-cover" />}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition" />
            </button>
          ))}
        </div>

        <button
          onClick={()=>fileRef.current?.click()}
          data-testid="vault-add-btn"
          className="mt-8 w-full py-5 rounded-3xl bg-gradient-to-r from-[#E60000] to-[#660000] hover:brightness-110 transition font-bold tracking-wide shadow-[0_0_30px_rgba(230,0,0,0.3)] active:scale-[0.98] flex items-center justify-center gap-2"
        >
          <Plus size={20} /> {t("vault.addMedia")}
        </button>
        <input ref={fileRef} onChange={onFile} type="file" accept="image/*,video/*" hidden data-testid="vault-file-input" />

        <p className="mt-6 text-center text-zinc-600 text-xs">{t("vault.maxSize")}</p>
      </div>

      {viewing && (
        <div className="fixed inset-0 bg-black/95 z-50 flex flex-col" onClick={()=>setViewing(null)} data-testid="vault-viewer">
          <button onClick={()=>setViewing(null)} className="absolute top-5 right-5 text-zinc-400 hover:text-white p-2" data-testid="vault-viewer-close">
            <X size={28} />
          </button>
          <button onClick={(e)=>{e.stopPropagation(); remove(viewing.id); setViewing(null);}} className="absolute top-5 left-5 text-zinc-400 hover:text-[#FF1A1A] p-2" data-testid={`vault-delete-${viewing.id}`}>
            <Trash2 size={22} />
          </button>
          <div className="flex-1 flex items-center justify-center p-6" onClick={(e)=>e.stopPropagation()}>
            {viewing.kind === "video"
              ? <video src={viewing.data_url} controls className="max-h-full max-w-full rounded-xl" />
              : <img src={viewing.data_url} alt="" className="max-h-full max-w-full rounded-xl" />}
          </div>
        </div>
      )}
    </div>
  );
}
